import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { StatusBadge } from '@/components/common/StatusBadge';
import { Package } from 'lucide-react';

interface TopWithdrawnProductsTableProps {
  withdrawals: any[];
  productById: Record<string, any>;
  periodLabel?: string;
}

export const TopWithdrawnProductsTable: React.FC<TopWithdrawnProductsTableProps> = ({ 
  withdrawals, 
  productById,
  periodLabel = '' 
}) => {

  const topWithdrawnProducts = useMemo(() => {
    const productRetraitCount = withdrawals.reduce((acc, retrait) => {
      const productId = retrait.product_id;
      const quantity = Number(retrait.quantity_withdrawn) || 0;
      acc[productId] = (acc[productId] || 0) + quantity;
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(productRetraitCount)
      .map(([productId, count]) => ({
        product: productById[productId],
        count
      }))
      .filter(item => item.product)
      .sort((a, b) => Number(b.count) - Number(a.count))
      .slice(0, 10);
  }, [withdrawals, productById]);

  return (
    <Card className="transition-all duration-200 hover:-translate-y-1 hover:shadow-md flex flex-col max-h-[800px]">
      <CardHeader className="flex-none sticky top-0 z-10 bg-background border-b">
        <CardTitle className="flex items-center gap-2">
          <Package className="w-5 h-5" />
          Produits les plus retirés {periodLabel}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-1 overflow-auto">
        <div className="sr-only" aria-live="polite">
          Tableau des 10 produits les plus retirés {periodLabel}. {topWithdrawnProducts.length} produits affichés. 
          {topWithdrawnProducts.map((item, index) => 
            `Position ${index + 1}: ${item.product.name}, ${item.count} unités retirées, stock actuel: ${item.product.quantity}`
          ).join('. ')}
        </div>
        <div className="overflow-x-auto">
          <Table aria-label={`Produits les plus retirés ${periodLabel}`} role="table">
            <caption className="sr-only">
              Top 10 des produits les plus retirés {periodLabel}. Ce tableau affiche le rang, le nom du produit, la quantité retirée et le stock actuel.
            </caption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col">Rang</TableHead>
                <TableHead scope="col">Produit</TableHead>
                <TableHead scope="col">Quantité retirée</TableHead>
                <TableHead scope="col">Stock actuel</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {topWithdrawnProducts.map((item, index) => (
                <TableRow key={item.product.id}>
                  <TableCell scope="row">#{index + 1}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{item.product.name}</span>
                      {item.product.cycle_de_vie && <StatusBadge status={item.product.cycle_de_vie} />}
                    </div>
                  </TableCell>
                  <TableCell className="text-red-600 font-medium">-{Number(item.count)}</TableCell>
                  <TableCell>
                    <span className={item.product.quantity <= item.product.min_threshold ? 'text-red-600 font-medium' : ''}>
                      {item.product.quantity}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {topWithdrawnProducts.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              Aucun retrait trouvé pour cette période
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};